import React, { useEffect, useState } from "react";
import axios from "axios";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import App from "./App";
import AdminAccessPanel from "./AdminAccessPanel";
import { loginRequest } from "./authConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginScreen() {
  const { instance } = useMsal();

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 text-white">
      <div className="w-full max-w-md rounded-xl bg-gray-800 border border-gray-700 p-8 text-center shadow-xl">
        <h1 className="text-2xl font-bold mb-3">Dashboard Audio Axis</h1>
        <p className="text-gray-300 mb-6">
          Connectez-vous avec votre compte Microsoft pour accéder à la gestion audio.
        </p>
        <button
          className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 px-5 py-3 font-semibold"
          onClick={() => instance.loginRedirect(loginRequest)}
        >
          Se connecter avec Microsoft
        </button>
      </div>
    </div>
  );
}

export default function AuthenticatedApp() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [ready, setReady] = useState(false);
  const [rbac, setRbac] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) {
      setReady(false);
      setRbac(null);
      setShowAdmin(false);
      return undefined;
    }

    let cancelled = false;

    const interceptor = axios.interceptors.request.use(async (config) => {
      const token = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token.accessToken}`;
      return config;
    });

    const initializeSession = async () => {
      try {
        const response = await axios.get(`${API}/rbac/me`);
        if (!cancelled) {
          setRbac(response.data);
        }
      } catch (error) {
        console.error("Unable to load RBAC profile", error);
        if (!cancelled) {
          setRbac({ admin: false, roles: [], permissions: [] });
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };

    initializeSession();

    return () => {
      cancelled = true;
      axios.interceptors.request.eject(interceptor);
    };
  }, [accounts, instance, isAuthenticated]);

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        Initialisation de la session Microsoft...
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-3 right-3 z-50 flex items-center gap-3 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-white shadow-lg">
        <span className="hidden sm:inline text-sm text-gray-300">
          {accounts[0]?.name || accounts[0]?.username}
        </span>
        {rbac?.admin && (
          <button
            className="text-sm rounded bg-blue-700 hover:bg-blue-600 px-3 py-1"
            onClick={() => setShowAdmin(true)}
          >
            Administration
          </button>
        )}
        <button
          className="text-sm rounded bg-gray-700 hover:bg-gray-600 px-3 py-1"
          onClick={() => instance.logoutRedirect({ account: accounts[0] })}
        >
          Déconnexion
        </button>
      </div>

      {showAdmin && rbac?.admin && (
        <AdminAccessPanel onClose={() => setShowAdmin(false)} />
      )}

      <App backendUrl={BACKEND_URL} />
    </>
  );
}
